import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Modal, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import swal from "sweetalert";
import { FormSkeleton, InlineLoader, TableSkeletonRows } from "../loader";
import { aiService } from "../../service/api.service";
import { EmptyTableState } from "../common/EmptyTableState";
import {
  StyledTable,
  StyledTableHeader,
  StyledTableCell,
  StyledTableRow,
} from "../common/StyledTable";
import { StatusBadge } from "../common/StatusBadge";

const USER_INFO_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "subscription", label: "Subscription" },
  { key: "token", label: "Token Information" },
  { key: "api", label: "API Configuration" },
  { key: "agents", label: "AI Agents", countKey: "agents" },
  { key: "phones", label: "Phone Numbers" },
];

function formatTokenCount(value) {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return new Intl.NumberFormat("en-US").format(numberValue);
}

const SUBSCRIPTION_STATUS = {
  active: { label: "Active", tone: "success" },
  scheduled: { label: "Queued", tone: "info" },
  completed: { label: "Expired", tone: "muted" },
  expired: { label: "Expired", tone: "muted" },
  cancelled: { label: "Cancelled", tone: "danger" },
};

function getSubscriptionStatus(sub) {
  return SUBSCRIPTION_STATUS[sub?.status] || { label: "-", tone: "muted" };
}

export default function UserInfo() {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [totalAgents, setTotalAgents] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState({ agent_usage: [] });
  const [usageSummaryLoading, setUsageSummaryLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");

  const handleUnauthorized = () => {
    swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
      localStorage.clear();
      window.location.href = "/";
    });
  };

  const getUser = useCallback(async (id, name, email) => {
    setUserLoading(true);
    try {
      const res = await aiService.getUserInfo(id);
      setUser(res?.data?.data || { _id: id, name, email });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: "Failed to load user details." });
      }
    } finally {
      setUserLoading(false);
    }
  }, []);

  const getAgents = useCallback(async (id) => {
    setAgentsLoading(true);
    try {
      const res = await aiService.getAgents({
        user_id: id,
        all: false,
        page: 1,
        page_size: 100,
      });
      setAgents(res?.data?.data || []);
      setTotalAgents(res?.data?.total_count || 0);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: "Failed to load AI agents." });
      }
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  const loadSubscriptions = useCallback(async (id) => {
    setHistoryLoading(true);
    try {
      const res = await aiService.getUserSubscriptions(id);
      setSubscriptions(res?.data?.data || []);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: "Failed to load subscription history." });
      }
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadUsageSummary = useCallback(async (id) => {
    if (!id) return;
    setUsageSummaryLoading(true);
    try {
      const res = await aiService.getUsageSummary(id, 30);
      setUsageSummary(res?.data || { agent_usage: [] });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        setUsageSummary({ agent_usage: [] });
      }
    } finally {
      setUsageSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = location.state?.userId || searchParams.get("user_id");
    const name =
      location.state?.userName || searchParams.get("user_name") || "";
    const email =
      location.state?.userEmail || searchParams.get("user_email") || "";

    if (!id) {
      swal({
        icon: "error",
        text: "Please select a user to view their details.",
      }).then(() => {
        navigate("/company-list");
      });
      return;
    }

    if (location.state?.user) {
      setUser(location.state.user);
    }
    getUser(id, name, email);
    getAgents(id);
    loadSubscriptions(id);
  }, [location, navigate, getUser, getAgents, loadSubscriptions]);

  useEffect(() => {
    if (activeSection === "token" && user?._id) {
      loadUsageSummary(user._id);
    }
  }, [activeSection, user?._id, loadUsageSummary]);

  const pageTitle =
    user?.name ||
    location.state?.userName ||
    new URLSearchParams(location.search).get("user_name") ||
    "User Info";

  const openAgentInfo = (agent) => {
    const query = new URLSearchParams({
      agent_id: agent._id,
      user_id: user?._id || agent.user || "",
      from: "company-info",
    });
    if (agent.name) query.append("agent_name", agent.name);
    if (user?.name) query.append("user_name", user.name);
    if (user?.email) query.append("user_email", user.email);

    navigate(`/agent-info?${query.toString()}`, {
      state: {
        agent,
        agentId: agent._id,
        agentName: agent.name,
        userId: user?._id || agent.user,
        userName: user?.name || "",
        userEmail: user?.email || "",
        fromUserInfo: true,
        returnToAllAgents: false,
      },
    });
  };

  const createAgent = () => {
    navigate("/agent-create", {
      state: {
        userId: user?._id,
        userName: user?.name,
        userEmail: user?.email,
        returnToAllAgents: false,
        returnTo: `${location.pathname}${location.search}`,
        fromUserInfo: true,
      },
    });
  };

  const openAssignNumberModal = (agent) => {
    navigate("/assign-numbers", {
      state: { openAgentId: agent._id },
    });
  };

  const openAddSubscription = () => {
    const query = new URLSearchParams({ user_id: user?._id || "" });
    if (user?.name) query.append("user_name", user.name);
    if (user?.email) query.append("user_email", user.email);

    navigate(`/add-subscription?${query.toString()}`, {
      state: {
        userId: user?._id,
        userName: user?.name,
        userEmail: user?.email,
      },
    });
  };

  const subscription = user?.subscription || {};
  const activeSubscription = subscriptions.find((sub) => sub.status === "active");
  const queuedSubscriptions = subscriptions.filter((sub) => sub.status === "scheduled");
  const queuedSubscription = queuedSubscriptions[0];
  const currentSubscription = activeSubscription || subscription;
  const currentStatus = activeSubscription
    ? getSubscriptionStatus(activeSubscription)
    : subscription?.type
      ? { label: "Active", tone: "success" }
      : { label: "-", tone: "muted" };
  const assignedPhoneCount = agents.filter((agent) => agent.phone).length;
  const tokenInfo = user?.tokenInfo || null;
  const remainingTokens =
    tokenInfo &&
    Number.isFinite(Number(tokenInfo.total_tokens)) &&
    Number.isFinite(Number(tokenInfo.used_tokens))
      ? Math.max(Number(tokenInfo.total_tokens) - Number(tokenInfo.used_tokens), 0)
      : null;
  const apiConfiguration = user?.apiConfiguration || {};
  const apiValidationSchema = Yup.object().shape({
    openAiApi: Yup.string().trim(),
    twilioSid: Yup.string().trim(),
    twilioSecretKey: Yup.string().trim(),
  });

  async function handleCancelActive() {
    const nextQueuedSubscription = queuedSubscriptions[0];
    const confirmText = nextQueuedSubscription
      ? `Are you sure you want to cancel the active subscription? The next queued subscription (${nextQueuedSubscription.type}) will be activated immediately.`
      : "Are you sure you want to cancel the active subscription? There is no queued subscription to activate next.";

    const confirmed = await swal({
      title: "Confirmation",
      text: confirmText,
      icon: "warning",
      buttons: ["NO", "YES"],
    });
    if (!confirmed) return;

    setCancelling(true);
    try {
      const res = await aiService.cancelActiveSubscription(user._id);
      await swal({
        icon: "success",
        text: res?.data?.message || "Active subscription cancelled.",
      });
      await loadSubscriptions(user._id);
      await getUser(user._id, user.name, user.email);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({
          icon: "error",
          text:
            error?.response?.data?.message ||
            "Unable to cancel the active subscription.",
        });
      }
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="user-info-page-header">
            {userLoading && !user ? (
              <FormSkeleton rows={2} />
            ) : (
              <>
                <div>
                  <h1 className="company-page-title">{pageTitle}</h1>
                  <p className="company-page-subtitle">
                    {user?.email || "-"}
                    {` · ${totalAgents} AI agent${totalAgents === 1 ? "" : "s"}`}
                  </p>
                </div>
                <StatusBadge
                  label={user?.is_active ? "Active" : "Deactivated"}
                  tone={user?.is_active ? "success" : "danger"}
                />
              </>
            )}
          </div>

          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="user-info-summary-grid">
                <div className="user-info-summary-card">
                  <span>Company access</span>
                  <div>
                    <strong>{totalAgents}</strong>
                    <small>AI agents</small>
                  </div>
                  <div>
                    <strong>{assignedPhoneCount}</strong>
                    <small>Phone numbers</small>
                  </div>
                </div>
                <div className="user-info-summary-card user-info-summary-card-wide">
                  <div className="user-info-summary-card-head">
                    <span>Subscription</span>
                    <StatusBadge
                      label={currentStatus.label}
                      tone={currentStatus.tone}
                    />
                  </div>
                  <div>
                    <strong>{currentSubscription?.type || "Trial"}</strong>
                    <small>Type</small>
                  </div>
                  <div>
                    <strong>
                      {currentSubscription?.subscriptionStartDate ||
                        currentSubscription?.subscriptionStarts ||
                        "-"}
                    </strong>
                    <small>Start</small>
                  </div>
                  <div>
                    <strong>
                      {currentSubscription?.subscriptionEndDate ||
                        currentSubscription?.renewalDate ||
                        currentSubscription?.trialEndDate ||
                        "-"}
                    </strong>
                    <small>End</small>
                  </div>
                </div>
              </div>

              <div
                className="user-info-section-nav"
                role="tablist"
                aria-label="User information sections"
              >
                {USER_INFO_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    role="tab"
                    className={`user-info-section-tab ${activeSection === section.key ? "active" : ""}`}
                    aria-selected={activeSection === section.key}
                    onClick={() => setActiveSection(section.key)}
                  >
                    <span>{section.label}</span>
                    {section.countKey === "agents" ? (
                      <span className="user-info-section-count">
                        {totalAgents}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>

              <div className="user-info-section-panel" role="tabpanel">
                {activeSection === "overview" ? (
                  <div className="create-user-card user-info-light-card">
                    <div className="create-user-card-header user-info-card-heading">
                      <h2>Company overview</h2>
                    </div>
                    <div className="create-user-card-body">
                      {userLoading && !user ? (
                        <FormSkeleton rows={4} />
                      ) : (
                        <Row>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">
                              ACCOUNT TYPE
                            </span>
                            <h6>{subscription.type || "Trial"}</h6>
                          </Col>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">
                              ORGANIZATION ID
                            </span>
                            <h6>{user?._id || "-"}</h6>
                          </Col>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">
                              AI AGENTS
                            </span>
                            <h6>{totalAgents}</h6>
                          </Col>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">NAME</span>
                            <h6>{user?.name || "-"}</h6>
                          </Col>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">EMAIL</span>
                            <h6>{user?.email || "-"}</h6>
                          </Col>
                          <Col sm={6} className="user-info-detail-item">
                            <span className="text-secondary">PHONE</span>
                            <h6>
                              {user?.phone || user?.phone_number || "-"}
                            </h6>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                ) : null}

                {activeSection === "subscription" ? (
                  <>
                  <div className="create-user-card user-info-light-card">
                    <div className="create-user-card-header user-info-card-heading">
                      <h2>Active Subscription</h2>
                      <div className="user-info-card-heading-actions">
                        <Button
                          type="button"
                          variant="primary"
                          className="agent-primary-btn subscription-add-icon-btn"
                          onClick={openAddSubscription}
                          disabled={!user}
                          title="Add subscription"
                          aria-label="Add subscription"
                        >
                          <i className="fa fa-plus" aria-hidden="true"></i>
                        </Button>
                        {activeSubscription ? (
                          <Button
                            type="button"
                            variant="outline-danger"
                            className="subscription-cancel-btn subscription-cancel-icon-btn"
                            onClick={handleCancelActive}
                            disabled={!user || cancelling}
                            title="Cancel active subscription"
                            aria-label="Cancel active subscription"
                          >
                            {cancelling ? (
                              <InlineLoader />
                            ) : (
                              <i className="fa fa-trash" aria-hidden="true"></i>
                            )}
                          </Button>
                        ) : null}
                        {/* Subscription edit is intentionally disabled for now.
                        <Button
                          type="button"
                          variant="light"
                          className="agent-icon-btn"
                          title="Edit subscription"
                          onClick={() => setEditSection("subscription")}
                          disabled={!user}
                        >
                          <i className="fa fa-pencil" aria-hidden="true"></i>
                        </Button>
                        */}
                      </div>
                    </div>
                    <div className="create-user-card-body">
                      <Row>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">
                            SUBSCRIPTION TYPE
                          </span>
                          <h6>{activeSubscription?.type || "-"}</h6>
                        </Col>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">REFERENCE</span>
                          <h6>{activeSubscription?.contractRef || "-"}</h6>
                        </Col>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">
                            SUBSCRIPTION STARTS
                          </span>
                          <h6>
                            {activeSubscription?.subscriptionStartDate || "-"}
                          </h6>
                        </Col>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">
                            SUBSCRIPTION ENDS
                          </span>
                          <h6>
                            {activeSubscription?.subscriptionEndDate || "-"}
                          </h6>
                        </Col>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">PRICE</span>
                          <h6>
                            {activeSubscription?.subscriptionPrice || "-"}
                          </h6>
                        </Col>
                        <Col sm={6} className="user-info-detail-item">
                          <span className="text-secondary">ALLOWED AGENTS</span>
                          <h6>{activeSubscription?.allowedAgents || "-"}</h6>
                        </Col>
                        <Col sm={12} className="user-info-detail-item">
                          <span className="text-secondary">
                            INTERNAL REMARKS
                          </span>
                          <h6>{activeSubscription?.internalRemarks || "-"}</h6>
                        </Col>
                      </Row>
                      {!historyLoading && !activeSubscription ? (
                        <div className="subscription-queue-empty">
                          <img
                            src={require("../../assets/images/no-data.svg").default}
                            alt="No active subscription"
                          />
                          <h3>No active subscription.</h3>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="agent-info-section-heading subscription-history-heading">
                    <h5 className="company-page-title user-info-section-title">
                      Subscription History &amp; Queue
                    </h5>
                  </div>
                  <div className="audit-request-box premium-company-card subscription-history-card">
                    <StyledTable
                      responsive="sm"
                      className="UserListTable premium-user-list-table subscription-history-table"
                    >
                      <thead>
                        <StyledTableRow>
                          <StyledTableHeader>Type</StyledTableHeader>
                          <StyledTableHeader>Starts</StyledTableHeader>
                          <StyledTableHeader>Ends</StyledTableHeader>
                          <StyledTableHeader>Price</StyledTableHeader>
                          <StyledTableHeader>Status</StyledTableHeader>
                        </StyledTableRow>
                      </thead>
                      <tbody>
                        {historyLoading ? (
                          <TableSkeletonRows rows={3} columns={5} />
                        ) : subscriptions.length > 0 ? (
                          subscriptions.map((sub) => {
                            const status = getSubscriptionStatus(sub);
                            return (
                              <StyledTableRow key={sub._id}>
                                <StyledTableCell>
                                  {sub.type || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {sub.subscriptionStartDate || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {sub.subscriptionEndDate || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {sub.subscriptionPrice || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  <StatusBadge
                                    label={status.label}
                                    tone={status.tone}
                                  />
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })
                        ) : (
                          <EmptyTableState colSpan={5} />
                        )}
                      </tbody>
                    </StyledTable>
                  </div>
                </>
              ) : null}

                {activeSection === "token" ? (
                  <div className="create-user-card user-info-light-card">
                    <div className="create-user-card-header user-info-card-heading">
                      <h2>Token Information</h2>
                    </div>
                    <div className="create-user-card-body">
                      {userLoading && !user ? (
                        <FormSkeleton rows={2} />
                      ) : (
                        <>
                          <div className="row g-3 mb-4">
                            <div className="col-12 col-md-4">
                              <div className="border rounded p-3">
                                <div className="text-secondary mb-2" style={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                                  Total
                                </div>
                                <div className="h6 mb-0">
                                  {usageSummaryLoading ? (
                                    <div className="placeholder-glow">
                                      <span className="placeholder col-6" />
                                    </div>
                                  ) : (
                                    formatTokenCount(usageSummary?.total_tokens || 0)
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="border rounded p-3">
                                <div className="text-secondary mb-2" style={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                                  Chat
                                </div>
                                <div className="h6 mb-0">
                                  {usageSummaryLoading ? (
                                    <div className="placeholder-glow">
                                      <span className="placeholder col-6" />
                                    </div>
                                  ) : (
                                    formatTokenCount(usageSummary?.chat_tokens || 0)
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="border rounded p-3">
                                <div className="text-secondary mb-2" style={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                                  Call
                                </div>
                                <div className="h6 mb-0">
                                  {usageSummaryLoading ? (
                                    <div className="placeholder-glow">
                                      <span className="placeholder col-6" />
                                    </div>
                                  ) : (
                                    formatTokenCount(usageSummary?.call_tokens || 0)
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="overflow-hidden rounded border">
                            {usageSummaryLoading ? (
                              <TableSkeletonRows rows={3} columns={4} />
                            ) : (
                              <StyledTable responsive="sm" className="UserListTable">
                                <thead>
                                  <StyledTableRow>
                                    <StyledTableHeader>Agent</StyledTableHeader>
                                    <StyledTableHeader>Chat Tokens</StyledTableHeader>
                                    <StyledTableHeader>Call Tokens</StyledTableHeader>
                                    <StyledTableHeader>Total</StyledTableHeader>
                                  </StyledTableRow>
                                </thead>
                                <tbody>
                                  {(usageSummary.agent_usage || []).length > 0 ? (
                                    (usageSummary.agent_usage || []).map((agent) => (
                                      <StyledTableRow key={agent.agent_id || agent.agent_name}>
                                        <StyledTableCell>{agent.agent_name || "Unnamed Agent"}</StyledTableCell>
                                        <StyledTableCell>{formatTokenCount(agent.chat_tokens || 0)}</StyledTableCell>
                                        <StyledTableCell>{formatTokenCount(agent.call_tokens || 0)}</StyledTableCell>
                                        <StyledTableCell>{formatTokenCount(agent.total_tokens || 0)}</StyledTableCell>
                                      </StyledTableRow>
                                    ))
                                  ) : (
                                    <StyledTableRow>
                                      <StyledTableCell colSpan={4} className="text-center text-muted">
                                        No usage data yet.
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  )}
                                </tbody>
                              </StyledTable>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                {activeSection === "api" ? (
                  <div className="create-user-card user-info-light-card">
                    <div className="create-user-card-header user-info-card-heading">
                      <h2>API configuration</h2>
                      <Button
                        type="button"
                        variant="light"
                        className="agent-icon-btn"
                        title="Edit API keys"
                        onClick={() => setEditSection("api")}
                        disabled={!user}
                      >
                        <i className="fa fa-pencil" aria-hidden="true"></i>
                      </Button>
                    </div>
                    <div className="create-user-card-body">
                      <Row>
                        <Col md={12} className="user-info-detail-item">
                          <span className="text-secondary">OPENAI API KEY</span>
                          <h6 className="api-key-value">
                            {apiConfiguration.openAiApi || "-"}
                          </h6>
                        </Col>
                        <Col md={6} className="user-info-detail-item">
                          <span className="text-secondary">TWILIO SID</span>
                          <h6 className="api-key-value">
                            {apiConfiguration.twilioSid || "-"}
                          </h6>
                        </Col>
                        <Col md={6} className="user-info-detail-item">
                          <span className="text-secondary">
                            TWILIO SECRET KEY
                          </span>
                          <h6 className="api-key-value">
                            {apiConfiguration.twilioSecretKey || "-"}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </div>
                ) : null}

                {activeSection === "agents" ? (
                  <>
                    <div className="agent-info-section-heading">
                      <h5 className="company-page-title user-info-section-title">
                        AI Agents
                      </h5>
                      <Button
                        variant="primary"
                        className="agent-primary-btn"
                        onClick={createAgent}
                        disabled={!user}
                      >
                        Create Agent
                      </Button>
                    </div>
                    <div className="audit-request-box premium-company-card">
                      <StyledTable
                        responsive="sm"
                        className="UserListTable premium-user-list-table user-info-agents-table"
                      >
                        <thead>
                          <StyledTableRow>
                            <StyledTableHeader>Name</StyledTableHeader>
                            <StyledTableHeader>Purpose</StyledTableHeader>
                            <StyledTableHeader>Tone</StyledTableHeader>
                            <StyledTableHeader>Phone Number</StyledTableHeader>
                          </StyledTableRow>
                        </thead>
                        <tbody>
                          {agentsLoading ? (
                            <TableSkeletonRows rows={3} columns={4} />
                          ) : agents.length > 0 ? (
                            agents.map((a) => (
                              <StyledTableRow key={a._id}>
                                <StyledTableCell>
                                  <button
                                    type="button"
                                    className="user-name-link agent-name-link"
                                    onClick={() => openAgentInfo(a)}
                                  >
                                    {a.name}
                                  </button>
                                </StyledTableCell>
                                <StyledTableCell>
                                  {a.purpose || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {a.tone || "-"}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {a.phone || "-"}
                                </StyledTableCell>
                              </StyledTableRow>
                            ))
                          ) : (
                            <EmptyTableState colSpan={4} />
                          )}
                        </tbody>
                      </StyledTable>
                    </div>
                  </>
                ) : null}

                {activeSection === "phones" ? (
                  <>
                    <div className="agent-info-section-heading">
                      <h5 className="company-page-title user-info-section-title">
                        Phone Numbers
                      </h5>
                    </div>
                    <div className="audit-request-box premium-company-card">
                      <StyledTable
                        responsive="sm"
                        className="UserListTable premium-user-list-table user-info-phones-table"
                      >
                        <thead>
                          <StyledTableRow>
                            <StyledTableHeader>Phone Number</StyledTableHeader>
                            <StyledTableHeader>AI Agent</StyledTableHeader>
                            <StyledTableHeader>Actions</StyledTableHeader>
                          </StyledTableRow>
                        </thead>
                        <tbody>
                          {agentsLoading ? (
                            <TableSkeletonRows rows={3} columns={3} />
                          ) : agents.length > 0 ? (
                            agents.map((a) => (
                              <StyledTableRow key={a._id}>
                                <StyledTableCell>
                                  {a.phone ? (
                                    a.phone
                                  ) : (
                                    <StatusBadge
                                      label="Not Assigned"
                                      tone="muted"
                                    />
                                  )}
                                </StyledTableCell>
                                <StyledTableCell>{a.name}</StyledTableCell>
                                <StyledTableCell>
                                  <div className="agent-action-buttons">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light agent-icon-btn"
                                      title="Edit Phone Number"
                                      aria-label={`Edit phone number for ${a.name || "agent"}`}
                                      onClick={() => openAssignNumberModal(a)}
                                    >
                                      <i
                                        className="fa fa-pencil"
                                        aria-hidden="true"
                                      ></i>
                                    </button>
                                  </div>
                                </StyledTableCell>
                              </StyledTableRow>
                            ))
                          ) : (
                            <EmptyTableState colSpan={3} />
                          )}
                        </tbody>
                      </StyledTable>
                    </div>
                  </>
                ) : null}
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Subscription edit functionality is intentionally commented out.
      <Modal
        show={editSection === "subscription"}
        onHide={() => setEditSection(null)}
        className="premium-modal user-info-edit-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit subscription details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={{
              subscriptionType: subscription.type || "Trial",
              trialEndDate: subscription.trialEndDate || "",
              renewalDate: subscription.renewalDate || "",
              internalRemarks: subscription.internalRemarks || "",
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              try {
                const res = await aiService.updateUserDetails(user._id, {
                  subscriptionType: values.subscriptionType,
                  trialEndDate: values.trialEndDate,
                  renewalDate: values.renewalDate,
                  internalRemarks: values.internalRemarks,
                });
                setUser(res?.data?.data || user);
                setEditSection(null);
                swal({
                  icon: "success",
                  text: "Subscription details updated successfully.",
                });
              } catch (error) {
                if (error?.response?.status === 401) {
                  handleUnauthorized();
                } else {
                  swal({
                    icon: "error",
                    text:
                      error?.response?.data?.message ||
                      "Unable to update subscription details.",
                  });
                }
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, handleChange, handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <div className="create-user-card user-info-modal-card">
                  <div className="create-user-card-header">
                    <h2>Subscription details</h2>
                  </div>
                  <div className="create-user-card-body">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Subscription type</Form.Label>
                          <Form.Select
                            className="filter-select"
                            name="subscriptionType"
                            value={values.subscriptionType}
                            onChange={handleChange}
                          >
                            {SUBSCRIPTION_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {values.subscriptionType === "Trial"
                              ? "Trial end date"
                              : "Renewal date"}
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name={
                              values.subscriptionType === "Trial"
                                ? "trialEndDate"
                                : "renewalDate"
                            }
                            value={
                              values.subscriptionType === "Trial"
                                ? values.trialEndDate
                                : values.renewalDate
                            }
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Internal remarks</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="internalRemarks"
                            value={values.internalRemarks}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="create-user-actions">
                  <Button
                    type="button"
                    variant="link"
                    className="create-user-cancel-btn"
                    onClick={() => setEditSection(null)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="agent-primary-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <InlineLoader /> : "Save changes"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
      */}

      <Modal
        show={editSection === "api"}
        onHide={() => setEditSection(null)}
        className="premium-modal user-info-edit-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit API configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={{
              openAiApi: apiConfiguration.openAiApi || "",
              twilioSid: apiConfiguration.twilioSid || "",
              twilioSecretKey: apiConfiguration.twilioSecretKey || "",
            }}
            validationSchema={apiValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              try {
                const res = await aiService.updateUserDetails(user._id, {
                  openAiApi: values.openAiApi,
                  twilioSid: values.twilioSid,
                  twilioSecretKey: values.twilioSecretKey,
                });
                setUser(res?.data?.data || user);
                setEditSection(null);
                swal({
                  icon: "success",
                  text: "API configuration updated successfully.",
                });
              } catch (error) {
                if (error?.response?.status === 401) {
                  handleUnauthorized();
                } else {
                  swal({
                    icon: "error",
                    text:
                      error?.response?.data?.message ||
                      "Unable to update API configuration.",
                  });
                }
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="create-user-card user-info-modal-card">
                  <div className="create-user-card-header">
                    <h2>API configuration</h2>
                  </div>
                  <div className="create-user-card-body">
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>OpenAI API key</Form.Label>
                          <Form.Control
                            type="password"
                            name="openAiApi"
                            value={values.openAiApi}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.openAiApi && !!errors.openAiApi}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.openAiApi}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Twilio SID</Form.Label>
                          <Form.Control
                            name="twilioSid"
                            value={values.twilioSid}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.twilioSid && !!errors.twilioSid}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.twilioSid}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Twilio secret key</Form.Label>
                          <Form.Control
                            type="password"
                            name="twilioSecretKey"
                            value={values.twilioSecretKey}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.twilioSecretKey &&
                              !!errors.twilioSecretKey
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.twilioSecretKey}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="create-user-actions">
                  <Button
                    type="button"
                    variant="link"
                    className="create-user-cancel-btn"
                    onClick={() => setEditSection(null)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="agent-primary-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <InlineLoader /> : "Save changes"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </div>
  );
}
